import React from 'react'
import { Box, useModalContext } from '@ally/metronome-ui'
import { asCurrency } from '@ally/aos-utils'
import { Account } from '@ally/data-types-afg-accounts'
import styled from 'styled-components'
import MobileCardDetail from './MobileCardDetail'
import {
  isAccountBlocked,
  isMortgagePaidOffHandled,
  mortgageAccountAllytmlnText,
} from '../../utils'
import { DelinquentMortgageModal, PaidOffModal } from '../HomeLoans/Modals'
import {
  StyledAccountDetailsLink,
  TotalBalanceContainer,
} from './SharedMobileCardStyles'
import { DashDash } from '../Shared'

const StyledBoxWrapper = styled(Box)`
  cursor: pointer;
  /* make sure every child has the cursor pointer  */
  * {
    cursor: pointer;
  }
`

const TotalAvailableBalance: React.FC<{
  accounts: Account[]
}> = ({ accounts }) => {
  const total = accounts.reduce((sum: number, account: Account) => {
    if (isAccountBlocked(account) || isMortgagePaidOffHandled(account)) {
      return sum
    }
    // istanbul ignore next
    return (account.meta?.amountDue ?? 0) + sum
  }, 0)

  const isEveryAccountBlocked = accounts.every(isAccountBlocked)

  function getCurrency(): string | JSX.Element {
    if (isEveryAccountBlocked) {
      return (
        <DashDash assistiveText="Total not available. Call for more information." />
      )
    }
    return asCurrency(total)
  }

  return (
    <TotalBalanceContainer>
      <Box fontWeight="bold">Total</Box>
      <Box fontWeight="bold">{getCurrency()}</Box>
    </TotalBalanceContainer>
  )
}

const ContentWithModal: React.FC<{
  account: Account
  focusRef?: React.RefObject<HTMLButtonElement>
}> = ({ account, focusRef }) => {
  const { setOpen } = useModalContext()

  return (
    <StyledBoxWrapper>
      <MobileCardDetail
        account={account}
        openMortgageModal={(): void => setOpen(true)}
        focusRef={focusRef}
      />
    </StyledBoxWrapper>
  )
}

const MobileHomeLoansList: React.FC<{
  accounts: Account[]
}> = ({ accounts }) => {
  return (
    <>
      {accounts.map((account: Account) => {
        const key = `detail-${account.id}`
        const Content = (): JSX.Element => (
          <StyledBoxWrapper>
            <MobileCardDetail account={account} />
          </StyledBoxWrapper>
        )

        if (isAccountBlocked(account)) {
          return (
            <DelinquentMortgageModal
              trigger={<ContentWithModal account={account} />}
              key={key}
            />
          )
        }

        if (isMortgagePaidOffHandled(account)) {
          return (
            <PaidOffModal
              key={key}
              account={account}
              trigger={(ref): JSX.Element => (
                <ContentWithModal account={account} focusRef={ref} />
              )}
            />
          )
        }

        return (
          <StyledAccountDetailsLink
            key={account.id}
            account={account}
            text={Content()}
            allytm={mortgageAccountAllytmlnText(account)}
          />
        )
      })}
      <TotalAvailableBalance accounts={accounts} />
    </>
  )
}

export default MobileHomeLoansList
